/**
 * Upload de fichiers vers Cloudinary (sans backend, via un upload preset "unsigned").
 *
 * Configuration :
 *  - Cloud name : "dway3r8jz" (public, peut rester en dur)
 *  - Upload preset UNSIGNED : par défaut "dahira_unsigned"
 *    (créez-le dans Cloudinary → Settings → Upload → Add upload preset →
 *     Signing Mode: Unsigned). On peut le surcharger via la variable
 *     d'environnement REACT_APP_CLOUDINARY_PRESET sans toucher au code.
 */

export const CLOUDINARY_CLOUD_NAME =
  process.env.REACT_APP_CLOUDINARY_CLOUD_NAME || 'dway3r8jz';

export const CLOUDINARY_PRESET =
  process.env.REACT_APP_CLOUDINARY_PRESET || 'dahira_unsigned';

export const isCloudinaryConfigured = () =>
  Boolean(CLOUDINARY_CLOUD_NAME && CLOUDINARY_PRESET);

/**
 * Envoie un fichier (image) vers Cloudinary.
 * @param {File} file
 * @param {{ folder?: string, onProgress?: (pct:number)=>void }} [opts]
 * @returns {Promise<{ url, secureUrl, publicId, width, height, format, bytes }>}
 */
export const uploadToCloudinary = (file, opts = {}) =>
  new Promise((resolve, reject) => {
    if (!isCloudinaryConfigured()) {
      reject(new Error('Cloudinary non configuré (cloud name / preset manquant).'));
      return;
    }

    const url = `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/auto/upload`;
    const form = new FormData();
    form.append('file', file);
    form.append('upload_preset', CLOUDINARY_PRESET);
    if (opts.folder) form.append('folder', opts.folder);

    const xhr = new XMLHttpRequest();
    xhr.open('POST', url);

    if (opts.onProgress) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) opts.onProgress(Math.round((e.loaded / e.total) * 100));
      };
    }

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        const r = JSON.parse(xhr.responseText);
        resolve({
          url: r.secure_url,
          secureUrl: r.secure_url,
          publicId: r.public_id,
          width: r.width,
          height: r.height,
          format: r.format,
          bytes: r.bytes,
          resourceType: r.resource_type, // 'image' | 'video'
          duration: r.duration || null   // secondes (vidéos)
        });
      } else {
        let msg = 'Échec de l\'upload Cloudinary';
        try { msg = JSON.parse(xhr.responseText).error?.message || msg; } catch (e) { /* ignore */ }
        reject(new Error(msg));
      }
    };
    xhr.onerror = () => reject(new Error('Erreur réseau pendant l\'upload.'));
    xhr.send(form);
  });

/**
 * Construit une URL de vignette optimisée à partir d'un publicId.
 * (transformations Cloudinary : recadrage + qualité/format auto)
 */
export const cloudinaryThumb = (publicId, { w = 400, h = 400 } = {}) =>
  `https://res.cloudinary.com/${CLOUDINARY_CLOUD_NAME}/image/upload/c_fill,w_${w},h_${h},q_auto,f_auto/${publicId}`;

/**
 * Vignette (poster) d'une vidéo : 1ʳᵉ image (so_0) recadrée, servie en JPG.
 */
export const cloudinaryVideoPoster = (publicId, { w = 500, h = 500 } = {}) =>
  `https://res.cloudinary.com/${CLOUDINARY_CLOUD_NAME}/video/upload/c_fill,w_${w},h_${h},so_0,q_auto,f_jpg/${publicId}.jpg`;

/**
 * URL de lecture optimisée d'une vidéo (qualité/format auto, largeur plafonnée).
 * Cloudinary transcode à la volée → lecture légère quel que soit l'original.
 */
export const cloudinaryVideo = (publicId, { w = 1280 } = {}) =>
  `https://res.cloudinary.com/${CLOUDINARY_CLOUD_NAME}/video/upload/q_auto,c_limit,w_${w}/${publicId}.mp4`;

/**
 * Construit une URL qui force le téléchargement de l'image d'origine
 * (transformation Cloudinary `fl_attachment`). Un nom de fichier lisible
 * peut être fourni (slugifié pour rester sûr dans une URL).
 */
export const cloudinaryDownloadUrl = (publicId, filename) => {
  const slug = (filename || 'photo')
    .toString()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '') // retire les accents
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase() || 'photo';
  return `https://res.cloudinary.com/${CLOUDINARY_CLOUD_NAME}/image/upload/fl_attachment:${slug}/${publicId}`;
};
